/**
 * Theme and density.
 *
 * DESIGN_LANGUAGE.md §2.4 asks for three theme settings — `light`, `dark`,
 * `system` — with `system` following `prefers-color-scheme` *without a reload*,
 * and for the preference to be stored per user on the server so it follows
 * across devices, with a local fallback that prevents a flash of the wrong
 * theme on first paint.
 *
 * What is built here: the three settings, the live `system` behaviour, the
 * attribute mechanism, and the local fallback (the inline script in
 * `index.html`, which this module keeps in sync).
 *
 * What is **not** built, and why: the server-side half. There is no endpoint
 * for it — `GET /v1/users/me` returns `id`, `username`, `display_name`,
 * `primary_email`, `email_verified_at`, `state`, `personal_org_id` and
 * `created_at`, and there is no `PATCH /v1/users/me` at all. So the preference
 * is per-browser until a user-preferences endpoint exists. Recorded in
 * web/README.md under known limitations rather than faked with a value this
 * client invents and no server ever sees.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type ThemeSetting = 'light' | 'dark' | 'system';
export type Density = 'compact' | 'comfortable';

const THEME_KEY = 'exigit.theme';
const DENSITY_KEY = 'exigit.density';

interface AppearanceApi {
  theme: ThemeSetting;
  setTheme: (theme: ThemeSetting) => void;
  /** What `theme` resolves to right now — `system` followed to its answer. */
  resolvedTheme: 'light' | 'dark';
  density: Density;
  setDensity: (density: Density) => void;
}

const AppearanceContext = createContext<AppearanceApi | null>(null);

export function useAppearance(): AppearanceApi {
  const context = useContext(AppearanceContext);
  if (!context) {
    throw new Error('useAppearance must be used inside <AppearanceProvider>.');
  }
  return context;
}

/**
 * Read a stored preference.
 *
 * Every access is guarded. `localStorage` throws rather than returning null in
 * a few real situations — Safari's private mode historically, a browser with
 * site data blocked by enterprise policy — and an exception here would take
 * down the whole application before it painted, over a theme.
 */
function read<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const stored = localStorage.getItem(key);
    return allowed.includes(stored as T) ? (stored as T) : fallback;
  } catch {
    return fallback;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // The preference applies for this session and is not remembered. That is
    // the whole consequence, and it is not worth telling the user about.
  }
}

export function AppearanceProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemeSetting>(() =>
    read(THEME_KEY, ['light', 'dark', 'system'] as const, 'system'),
  );
  const [density, setDensityState] = useState<Density>(() =>
    read(DENSITY_KEY, ['compact', 'comfortable'] as const, 'compact'),
  );
  const [systemIsDark, setSystemIsDark] = useState(
    () => window.matchMedia('(prefers-color-scheme: dark)').matches,
  );

  // Follow the system setting while it is selected — and while it is not,
  // because switching back to `system` should already know the answer.
  useEffect(() => {
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event: MediaQueryListEvent) => {
      setSystemIsDark(event.matches);
    };
    query.addEventListener('change', onChange);
    return () => {
      query.removeEventListener('change', onChange);
    };
  }, []);

  const resolvedTheme: 'light' | 'dark' =
    theme === 'system' ? (systemIsDark ? 'dark' : 'light') : theme;

  // `system` removes the attribute rather than writing the resolved value, so
  // that the `@media (prefers-color-scheme: dark)` block in tokens.semantic.css
  // is what answers. Writing the resolved value would work until the user
  // changed their OS setting with the tab in the background.
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'system') {
      root.removeAttribute('data-theme');
    } else {
      root.setAttribute('data-theme', theme);
    }
  }, [theme]);

  useEffect(() => {
    document.documentElement.setAttribute('data-density', density);
  }, [density]);

  const setTheme = useCallback((next: ThemeSetting) => {
    setThemeState(next);
    write(THEME_KEY, next);
  }, []);

  const setDensity = useCallback((next: Density) => {
    setDensityState(next);
    write(DENSITY_KEY, next);
  }, []);

  const value = useMemo<AppearanceApi>(
    () => ({ theme, setTheme, resolvedTheme, density, setDensity }),
    [theme, setTheme, resolvedTheme, density, setDensity],
  );

  return <AppearanceContext.Provider value={value}>{children}</AppearanceContext.Provider>;
}
